/**
 * /rentals/assets/:assetId — one rental asset on its own page: its details
 * (`GET /surface-rentals/assets/:id`, which adds `active_rentals` to the row)
 * and the edit form. It was a slide-out panel over the Rental Equipment list
 * until split panes and drawers beside a list were banned (CLAUDE.md). Save
 * sits on the band, top right, and returns to the list with its chip, category
 * and search as they were left, as the panel's Save closed it.
 *
 * WHEN THE FULL RECORD DOES NOT LOAD the panel kept the list's row on screen
 * and said so, and so does this page: if the list is in the cache, its row is
 * shown under a note that it is the list's copy, and the form edits it. With
 * no list row to fall back on, the page says it could not load the asset, or
 * that there is no such asset (a 404).
 */
import React, { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { api, getApiErrorMessage } from "../../api/client";
import ManageCategoriesDialog from "../../components/rentals/ManageCategoriesDialog";
import { PageHeaderBand } from "../../components/layout/PageHeaderBand";
import { LoadFailureNotice } from "../../components/ui/LoadFailureNotice";
import { useTenantTimeZone } from "../../hooks/useTenantTimeZone";
import { unwrap } from "../../lib/api-envelope";
import { formatCalendarDate, formatInstantDay } from "../../lib/calendar-date";
import { LOAD_FAILURE_QUERY_OPTIONS, isNotFound, queryFailureError, queryLoadFailure } from "../../lib/load-failure";
import { useBackToList } from "../downhole/recordPage";
import {
  type Asset,
  type AssetForm,
  AssetFormFields,
  RENTAL_ASSETS_KEY,
  StatusBadge,
  assetBody,
  formFrom,
  normalizeAsset,
  useAssetCategoryNames,
  useRentalAssets,
} from "./rental-asset";

const LIST = "/rentals/assets";

const PANEL = "rounded-fi-panel border border-fieldiq-border bg-fieldiq-card";

function PanelHeading({ eyebrow, title }: { eyebrow: string; title: string }) {
  return (
    <header className="border-b border-fieldiq-border px-4 py-4">
      <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">{eyebrow}</p>
      <h2 className="mt-1 text-base font-extrabold tracking-tight text-fieldiq-text">{title}</h2>
    </header>
  );
}

export default function AssetDetailScreen() {
  const { assetId } = useParams<{ assetId: string }>();
  const back = useBackToList(LIST);
  const timeZone = useTenantTimeZone();
  const queryClient = useQueryClient();

  // Under the list's key, so the list's invalidation after a write refreshes it too.
  const detailQuery = useQuery({
    queryKey: [...RENTAL_ASSETS_KEY, assetId],
    queryFn: async () => {
      const raw = unwrap<Record<string, unknown>>((await api.rentals.assets.get(assetId as string)).data);
      return raw ? normalizeAsset(raw) : null;
    },
    enabled: !!assetId,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  // The list: the category picker offers the categories assets already carry,
  // and its row is what the page falls back to if the full record fails.
  const listQuery = useRentalAssets();
  const categoryNames = useAssetCategoryNames(listQuery.data);

  const failure = queryLoadFailure(detailQuery);
  const full = detailQuery.data ?? null;
  const listRow = listQuery.data?.find((a) => a.id === assetId) ?? null;
  const fallback = !full && !!failure && !isNotFound(queryFailureError(detailQuery)) ? listRow : null;
  const record: Asset | null = full ?? fallback;
  const loading = !record && detailQuery.isLoading;
  const notFound = !record && !detailQuery.isLoading && (isNotFound(queryFailureError(detailQuery)) || !failure);

  // The form starts from the full record, or from the list's row when that is
  // all there is, and is replaced once when the full record arrives. A later
  // background refetch does not overwrite what is being typed.
  const [editForm, setEditForm] = useState<AssetForm | null>(null);
  const formSource = useRef<"none" | "list" | "full">("none");
  useEffect(() => {
    if (full && formSource.current !== "full") {
      setEditForm(formFrom(full));
      formSource.current = "full";
    } else if (!full && fallback && formSource.current === "none") {
      setEditForm(formFrom(fallback));
      formSource.current = "list";
    }
  }, [full, fallback]);

  const [submitting, setSubmitting] = useState(false);
  const [manageCategoriesOpen, setManageCategoriesOpen] = useState(false);

  const handleEditSave = async () => {
    if (!record || !editForm) return;
    if (!editForm.tag.trim() || !editForm.name.trim() || !editForm.category) {
      toast.error("Asset Number, Name, and Category can't be blank");
      return;
    }
    const built = assetBody(editForm, record);
    if ("error" in built) {
      toast.error(built.error);
      return;
    }
    setSubmitting(true);
    try {
      await api.rentals.assets.update(record.id, built.body);
      toast.success("Asset updated");
      await queryClient.invalidateQueries({ queryKey: RENTAL_ASSETS_KEY });
      back();
    } catch (err) {
      // The page stays on the values the user entered. Writing them into the
      // cache made the list disagree with the server until the next refetch
      // silently reverted it.
      toast.error(getApiErrorMessage(err, "Failed to save asset"));
    } finally {
      setSubmitting(false);
    }
  };

  const specEntries = record?.specifications ? Object.entries(record.specifications).filter(([, v]) => v != null && v !== "") : [];

  return (
    <div data-testid="asset-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Rental equipment"
        title={record ? record.name : loading ? "Loading…" : "Asset"}
        onBack={back}
        backLabel="Back to rental equipment"
        actions={
          record && editForm ? (
            <button
              type="button"
              onClick={handleEditSave}
              disabled={submitting}
              className="fi-action fi-action--standard fi-action--primary disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <Save className="size-4" aria-hidden="true" />}
              Save
            </button>
          ) : undefined
        }
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {loading ? (
            <div className={`${PANEL} space-y-3 p-4`} aria-label="Loading the asset">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-4 animate-pulse rounded bg-fieldiq-dark/60" />
              ))}
            </div>
          ) : notFound ? (
            <p className={`${PANEL} px-4 py-6 text-sm text-fieldiq-text`} data-testid="asset-not-found">
              This asset was not found.
            </p>
          ) : !record ? (
            <div className={PANEL}>
              <LoadFailureNotice error={queryFailureError(detailQuery)} kind={failure ?? undefined} subject="this asset" />
            </div>
          ) : (
            /* Stacked, full width at every size: the details on top, the edit form
               below. No two panels sit side by side on a record page (operator,
               2026-10-20), the same rule as no list beside a detail. */
            <div className="space-y-4" data-testid="asset-record-stack">
              <section aria-labelledby="asset-details-title" className={`${PANEL} border-l-4 border-l-fi-amber`} data-testid="asset-details">
                <header className="border-b border-fieldiq-border px-4 py-4">
                  <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Rental equipment</p>
                  <h2 id="asset-details-title" className="mt-1 text-base font-extrabold tracking-tight text-fieldiq-text">
                    Asset details
                  </h2>
                </header>
                <dl className="space-y-2 p-4 text-sm">
                  {fallback && (
                    <p role="status" className="mb-2 rounded-fi-control border border-fieldiq-border bg-fieldiq-dark/50 p-2 text-xs text-fieldiq-muted">
                      Couldn&rsquo;t load this asset&rsquo;s full record. Showing what the list had.
                    </p>
                  )}
                  <div className="flex justify-between gap-3">
                    <dt className="text-fieldiq-muted">Status</dt>
                    <dd><StatusBadge status={record.status} /></dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-fieldiq-muted">Asset number</dt>
                    <dd className="font-mono text-fieldiq-text">{record.tag ?? "—"}</dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-fieldiq-muted">Serial number</dt>
                    <dd className="font-mono text-xs text-fieldiq-text">{record.serial_number ?? "—"}</dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-fieldiq-muted">Model</dt>
                    <dd className="text-fieldiq-text">{record.model ?? "—"}</dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-fieldiq-muted">Last PM</dt>
                    <dd className="text-fieldiq-text">{formatCalendarDate(record.last_pm_date) ?? "—"}</dd>
                  </div>
                  {specEntries.length > 0 && (
                    <div className="pt-2">
                      <dt className="mb-1 text-fieldiq-muted">Specifications</dt>
                      <dd className="space-y-1">
                        {specEntries.map(([k, v]) => (
                          <div key={k} className="flex justify-between gap-3 text-xs">
                            <span className="text-fieldiq-muted">{k}</span>
                            <span className="min-w-0 break-all text-right text-fieldiq-text">{typeof v === "object" ? JSON.stringify(v) : String(v)}</span>
                          </div>
                        ))}
                      </dd>
                    </div>
                  )}
                  {record.active_rentals && record.active_rentals.length > 0 && (
                    <div className="mt-3 rounded-fi-control border border-fieldiq-border bg-fieldiq-dark/40 p-3">
                      <dt className="mb-2 text-[10px] font-bold uppercase tracking-wider text-fieldiq-muted">On rent</dt>
                      <dd className="space-y-1 text-xs">
                        {record.active_rentals.map((r, i) => (
                          <div key={r.order_id ?? i} className="flex justify-between gap-3">
                            {/* The row carries only the order's id, which means nothing to a
                                reader, so it shows when the asset went out instead. */}
                            <span className="text-fieldiq-text">On rent</span>
                            <span className="text-fieldiq-muted">since {formatInstantDay(r.delivered_at, timeZone) ?? "—"}</span>
                          </div>
                        ))}
                      </dd>
                    </div>
                  )}
                </dl>
              </section>

              <section className={PANEL} data-testid="asset-edit">
                <PanelHeading eyebrow="Change this asset" title="Edit Asset" />
                <div className="p-4">
                  {editForm && (
                    <AssetFormFields
                      form={editForm}
                      setForm={(fn) => setEditForm((prev) => (prev ? fn(prev) : prev))}
                      idPrefix="edit-asset"
                      categoryNames={categoryNames}
                      onManageCategories={() => setManageCategoriesOpen(true)}
                    />
                  )}
                </div>
              </section>
            </div>
          )}
        </div>
      </section>

      <ManageCategoriesDialog open={manageCategoriesOpen} onOpenChange={setManageCategoriesOpen} />
    </div>
  );
}
